import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './PrivacyPolicyModal.css';

// Политика конфиденциальности: тексты лежат в locales/<язык>/privacy.json и подгружаются только при открытии.
// Язык текста можно переключить прямо здесь, независимо от языка приложения.

interface PrivacyPolicyModalProps {
  onClose: () => void;
}

interface Section {
  title: string;
  items: string[];
}

const LANGUAGES = [
  { code: 'ru', label: 'RU' },
  { code: 'en', label: 'EN' },
  { code: 'uk', label: 'UK' },
  { code: 'ka', label: 'KA' },
  { code: 'es', label: 'ES' },
  { code: 'tr', label: 'TR' },
  { code: 'vi', label: 'VI' },
  { code: 'th', label: 'TH' },
  { code: 'ar', label: 'AR' },
] as const;

const SUPPORTED = LANGUAGES.map((language) => language.code) as string[];

interface PolicyText {
  title: string;
  version: string;
  previous: string;
  language: string;
  summaryTitle: string;
  summary: string[];
  understand: string;
  close: string;
  sections: Section[];
}

// Тексты скачиваются по одному языку и запоминаются до перезагрузки страницы
const cache = new Map<string, PolicyText>();

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ onClose }) => {
  const { i18n } = useTranslation();
  const initial = SUPPORTED.includes(i18n.language.slice(0, 2)) ? i18n.language.slice(0, 2) : 'en';
  const [lang, setLang] = useState(initial);
  const [text, setText] = useState<PolicyText | null>(cache.get(initial) ?? null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const cached = cache.get(lang);
    if (cached) {
      setText(cached);
      setFailed(false);
      return undefined;
    }
    let cancelled = false;
    setFailed(false);
    fetch(`/locales/${lang}/privacy.json`)
      .then((response) => (response.ok ? (response.json() as Promise<PolicyText>) : Promise.reject(new Error(String(response.status)))))
      .then((data) => {
        cache.set(lang, data);
        if (!cancelled) setText(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Страница под окном не должна прокручиваться вместе с ним (в Safari это сдвигало окно)
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const ready = !!text;
  const sections = text?.sections ?? [];
  const summary = text?.summary ?? [];

  // В портале: внутри прокручиваемой страницы профиля «fixed» привязывался к ней и ехал вместе с ней
  return createPortal(
    <div className="privacy-modal-overlay" onClick={onClose}>
      <div className="privacy-modal-content" role="dialog" aria-modal="true" aria-labelledby="pp-title" lang={lang} dir={lang === 'ar' ? 'rtl' : 'ltr'} onClick={(e) => e.stopPropagation()}>
        <div className="privacy-modal-header">
          <div className="pp-head-text">
            <h2 id="pp-title">{text?.title ?? ''}</h2>
            {text && <span className="pp-version">{text.version}</span>}
          </div>
          <button type="button" className="privacy-modal-close" onClick={onClose} aria-label={text?.close ?? 'Close'}>×</button>
        </div>

        <div className="pp-langs" role="group" aria-label={text?.language ?? 'Language'}>
          {LANGUAGES.map((language) => (
            <button
              key={language.code}
              type="button"
              className={`pp-lang${lang === language.code ? ' pp-lang--active' : ''}`}
              aria-pressed={lang === language.code}
              onClick={() => setLang(language.code)}
            >
              {language.label}
            </button>
          ))}
        </div>

        <div className="privacy-modal-body">
          {failed && <p className="pp-previous">support@croupierassistant.app</p>}
          {ready && (
            <>
              <div className="pp-summary">
                <b>{text?.summaryTitle}</b>
                {summary.map((line) => <span key={line}>• {line}</span>)}
              </div>
              {sections.map((section) => (
                <details key={section.title} className="pp-section">
                  <summary>{section.title}</summary>
                  <ul>
                    {section.items.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </details>
              ))}
              <p className="pp-previous">{text?.previous}</p>
            </>
          )}
        </div>

        <div className="privacy-modal-actions">
          <button type="button" className="privacy-accept-button" onClick={onClose}>{text?.understand ?? 'OK'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PrivacyPolicyModal;
